import { useEffect, useRef } from "react";
import { useEtatInfos } from "./etatInfos.js";
import { CreditsPanel } from "./CreditsPanel.js";
import { FeuilleInfos } from "./FeuilleInfos.js";
import { raccourciPleinEcran } from "./logiqueInfos.js";
import { MenuContextuel } from "./MenuContextuel.js";
import { useInfos, usePleinEcran } from "./useInfos.js";
import { hote } from "./infosStyles.js";

/**
 * ============================================================================
 *  InfoMenu.tsx — Le menu d'informations et des crédits (remplace l'encart du moteur)
 * ----------------------------------------------------------------------------
 *  Référence : `sources/design/design_handoff_credits/`. Monté UNE fois, au-dessus de
 *  tout (visite libre, mode main du guide, bandes) : il porte les surcouches — menu
 *  contextuel sur ordinateur, feuille basse sur téléphone, fenêtre ou vue des crédits.
 *  Les boutons qui l'ouvrent (`BoutonInfos`) sont posés où le design les veut.
 *
 *  Il remplace le menu du clic droit du moteur (« Bienvenue… / Made in Annecy with ♥ »),
 *  qui n'est plus monté. Le raccourci F (plein écran) vit ici, tant qu'aucune surcouche
 *  n'est ouverte : ouverte, c'est elle qui le traite (`useClavierSurcouche`).
 * ==========================================================================*/

export function InfoMenu({ assetBaseUrl, surAccueil }: { assetBaseUrl: string; surAccueil: () => void }) {
  const infos = useInfos();
  const menu = useEtatInfos((e) => e.session.menu);
  const credits = useEtatInfos((e) => e.session.credits);
  const pleinEcran = usePleinEcran();

  useEffect(() => {
    if (menu || credits || !pleinEcran.disponible) return;
    const surTouche = (event: KeyboardEvent) => {
      const cible = event.target as HTMLElement | null;
      const saisie = !!cible && (cible.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(cible.tagName));
      if (event.repeat || !raccourciPleinEcran(event.key, event.ctrlKey || event.metaKey || event.altKey, saisie)) return;
      event.preventDefault();
      pleinEcran.basculer();
    };
    window.addEventListener("keydown", surTouche);
    return () => window.removeEventListener("keydown", surTouche);
  }, [menu, credits, pleinEcran]);

  // Clic droit sur ordinateur : le même menu, au curseur (il remplace celui du moteur). Sur téléphone
  // le menu contextuel natif n'existe pas (appui long) : rien à intercepter. Les champs de saisie gardent le leur.
  const { mobile, ouvrirAuPoint } = infos;
  useEffect(() => {
    if (mobile) return;
    const surMenuContextuel = (event: MouseEvent) => {
      const cible = event.target as HTMLElement | null;
      if (cible && (cible.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(cible.tagName))) return;
      event.preventDefault();
      if (!credits) ouvrirAuPoint(event.clientX, event.clientY);
    };
    window.addEventListener("contextmenu", surMenuContextuel);
    return () => window.removeEventListener("contextmenu", surMenuContextuel);
  }, [mobile, credits, ouvrirAuPoint]);

  // Quitter la visite (retour à l'accueil) avec le menu ouvert : rien ne doit rester ouvert au retour,
  // et la voix mise en pause par les crédits est rendue.
  // (Par une ref : la fonction change à chaque rendu, et son nettoyage ne doit s'exécuter qu'au démontage.)
  const toutFermer = useRef(infos.fermerTout);
  toutFermer.current = infos.fermerTout;
  useEffect(() => () => toutFermer.current(), []);

  return (
    <div style={hote}>
      {menu && !infos.mobile && <MenuContextuel surAccueil={surAccueil} />}
      {menu && infos.mobile && <FeuilleInfos surAccueil={surAccueil} />}
      {credits && <CreditsPanel assetBaseUrl={assetBaseUrl} />}
    </div>
  );
}
